import moment from "moment";
import { ReactElement } from "react";
import {
	LegendLabel,
	LikelihoodLabel,
	RiskMatrixCellData,
	SeverityLabel
} from "../Assessments";
import { ClientContact, ClientType } from "../Clients";
import {
	Address,
	ArchievedWithSyncModel,
	Links,
	Name,
	Social,
	TableQueryFilter,
	ZomentumEntities
} from "../common";
import { Activity, Comment } from "../Others/RecentActivityFeed";
import { BlockResponse, GOFBlockChanges } from "../ProposalBuilder/Block";
import {
	CustomField,
	CommonNameValuePair,
	QuickbooksTaxCode,
	QuickbooksTaxInfoList,
	CustomFieldType
} from "../Settings";
import { FormIdentifier } from "../UI/DynamicForm";
import {
	DocumentCommentBoxLabels,
	DocumentDefaultDocument,
	TaxCategory,
	TaxRegion,
	UserCompany,
	UserData
} from "../Users";
import { ChildEntityHydrationParams } from "../Utils";
import { DocumentApproval } from "./../DocumentApprovals/index";
import { DocumentRevision } from "./Revision";
import { EIcons } from "@atoms/ZIcon/interface";
import { DocumentDefaults } from "./Revision";

export const DefaultBlockPaginationLimit = 25;

export class QuoteTotal {
	monthly = 0;
	weekly = 0;
	annual = 0;
	one_time = 0;
	grand = 0;
	quarterly = 0;
	semi_annually = 0;
	total_cost?: number = 0;
	margin_percentage?: number = 0;
	margin_value?: number = 0;
}

export enum DiscountType {
	Flat = "flat",
	Percentage = "percentage"
}

export class DocumentCount {
	all = 0;
	pre_draft = 0;
	draft = 0;
	lost = 0;
	open = 0;
	sent = 0;
	won = 0;
	signature_in_progress = 0;
	viewed = 0;
	expired = 0;
	shared = 0;
	signed = 0;
	published = 0;
	unpublished = 0;
	requested = 0;
}

export enum DocumentStatus {
	Draft = "draft",
	Lost = "lost",
	Sent = "sent",
	Won = "won",
	Signature = "signature_in_progress",
	Viewed = "viewed",
	Expired = "expired",
	Shared = "shared",
	Signed = "signed",
	Published = "published",
	Unpublished = "unpublished",
	Requested = "requested"
	// usually a transient state, if its Pay and Sign, and paument is in progress or unsuccessful
	// and signature/approval is pending
}

export enum DocumentPinStatus {
	Locked = "locked",
	Unlocked = "unlocked",
	PinSent = "pin_sent"
}

export enum DocumentQuickbooksStatus {
	Synced = "synced",
	Pending = "pending",
	Unsynced = "unsynced"
}

export enum DocumentPinApproveStatus {
	PinIncorrect = "pin_incorrect",
	PinExpired = "pin_expired",
	PinApproved = "pin_approved",
	PinBlocked = "blocked"
}

export enum DocumentPinGenerationStatus {
	PinGenerationSuccessful = "pin_generation_success",
	PinGenerationFailed = "pin_generation_failure",
	PinGenerationBlocked = "pin_generation_blocked"
}

export interface DocumentPinGenerationRequest {
	document_id: string;
}
export interface DocumentPinGenerationResponse {
	last_generate_time: string;
	request_count: number;
	status: DocumentPinGenerationStatus;
}

export interface DocumentPinVerificationRequest {
	document_id: string;
	pin: string;
}

export interface VerifyDocumentPinRequest {
	document_id: string;
	pin: string;
}
export interface VerifyDocumentPinResponse {
	status: DocumentPinApproveStatus;
	incorrect_attempt: number;
	last_incorrect_attempt: string;
	approved_time?: Date;
}

export interface BulkDocumentStatusUpdate {
	key: "Lost" | "Won" | "Expired" | "Shared";
	value:
		| DocumentStatus.Lost
		| DocumentStatus.Won
		| DocumentStatus.Expired
		| DocumentStatus.Shared;
}
export interface OrderFormStatusUpdate {
	readonly key: "Published" | "Unpublished";
	readonly value: DocumentStatus.Published | DocumentStatus.Unpublished;
}

export const DocumentBulkUpdateValidStatus: BulkDocumentStatusUpdate[] = [
	{ key: "Lost", value: DocumentStatus.Lost },
	{ key: "Won", value: DocumentStatus.Won },
	{ key: "Expired", value: DocumentStatus.Expired },
	{ key: "Shared", value: DocumentStatus.Shared }
];

export const DocumentOrderFormValidStatus: OrderFormStatusUpdate[] = [
	{ key: "Published", value: DocumentStatus.Published },
	{ key: "Unpublished", value: DocumentStatus.Unpublished }
];

export interface DocumentBulkUpdateStatusData {
	notify_recipients: boolean;
	status: DocumentStatus;
}

export interface DocumentBulkUpdateStatus {
	document_id: string;
	update_request: DocumentBulkUpdateStatusData;
}

export interface DocumentBulkUpdateStatusRequest {
	documents: DocumentBulkUpdateStatus[];
}

export enum DocumentExpiryType {
	Relative = "relative",
	Absolute = "absolute"
}

export class DocumentOwner {
	email = "";
	name: Name = new Name();
	user_id = "";
}

export enum EmptyDocumentTemplateValue {
	Blank = "blank"
}

export class Reminder {
	reminder_type: string = DocumentExpiryType.Relative;
}

export class DocumentExpiry {
	expiry_type: string = DocumentExpiryType.Relative;
	relative_expiry = 30;
	absolute_expiry: Date = new Date(
		new Date().getTime() + 24 * 60 * 60 * 1000
	);
	send_reminder = false;

	constructor(days = 30) {
		this.absolute_expiry = new Date(
			moment().add(days, "days").toISOString()
		);
		this.relative_expiry = days;
	}
}

export class DocumentAutoReminder {
	auto_email_reminder = false;
	first_reminder_in = 1;
	repeat_email_reminder = false;
	repeat_reminder_in = 5;
}

// Documents will have multiple types going ahead (contracts etc), need to refactor the below when that happens
export enum DocumentType {
	PROPOSAL = "proposal",
	CONTRACT = "contract",
	QUOTE = "quote",
	SALES_PRESENTATION = "sales_presentation",
	ASSESSMENT = "assessment",
	QBR = "qbr",
	SPECIFIC_ORDER_FORM = "specific_order_form",
	GENERIC_ORDER_FORM = "generic_order_form"
}

export enum DocumentThumbnailType {
	PROPOSAL = "proposal",
	CONTRACT = "contract",
	QUOTE = "quote",
	ORDER_FORM = "order_form"
}

export enum QbrRepresentationStyle {
	HEATLH_STATUS = "health_status",
	RISK_MATRIX = "risk_matrix",
	BOTH = "both" // shown as "hybrid" representation style in the UI
}

export enum AssessmentRepresentationStyle {
	SEVERITY_LIKELIHOOD = "severity_likelihood",
	RISK_LEVEL = "risk_level"
}

export const DocumentThumbCardTypes = [
	DocumentThumbnailType.PROPOSAL,
	DocumentThumbnailType.CONTRACT,
	DocumentThumbnailType.QUOTE,
	DocumentThumbnailType.ORDER_FORM
];

export const DocumentTypeLabelMap: Record<DocumentType, string> = {
	[DocumentType.PROPOSAL]: "Proposal",
	[DocumentType.CONTRACT]: "Contract",
	[DocumentType.QUOTE]: "Quote",
	[DocumentType.SALES_PRESENTATION]: "Assessment",
	[DocumentType.ASSESSMENT]: "Assessment",
	[DocumentType.QBR]: "QBR",
	[DocumentType.GENERIC_ORDER_FORM]: "Generic Order Form",
	[DocumentType.SPECIFIC_ORDER_FORM]: "Specific Order Form"
};

export const DocumentTypeDataTestIdMap: Record<DocumentThumbnailType, string> =
	{
		[DocumentThumbnailType.PROPOSAL]: "proposal-doc-type",
		[DocumentThumbnailType.CONTRACT]: "contract-doc-type",
		[DocumentThumbnailType.QUOTE]: "quote-doc-type",
		[DocumentThumbnailType.ORDER_FORM]: "orderForm-doc-type"
	};

export const DocumentTypeEIconsMap: Record<DocumentThumbnailType, string> = {
	[DocumentThumbnailType.PROPOSAL]: EIcons.DOCPROPOSAL,
	[DocumentThumbnailType.CONTRACT]: EIcons.DOCCONTRACT,
	[DocumentThumbnailType.QUOTE]: EIcons.DOCQUOTE,
	[DocumentThumbnailType.ORDER_FORM]: EIcons.DOCORDERFORM
};

export interface DocumentActionConfigurations {
	newStatus?: DocumentStatus;
}

export enum EmptyMergeTagViewPreference {
	ReplaceWithEmptyString = "replace_with_empty_string",
	LeaveAsIs = "leave_as_is"
}

export interface PaymentData {
	form_url?: string;
	transaction_id?: number;
	last_checkout_session?: string;
}

export interface BillingAddress {
	address_line_1: string | null;
	address_line_2: string | null;
	city: string | null;
	country: string | null;
	phone: string | null;
	pincode: string | null;
	state: string | null;
}

export interface DocumentPayment {
	custom_amount?: number | null;
	custom_percentage?: number | null;
	data?: PaymentData | null;
	gateway?: string;
	is_optional?: boolean;
	payer?: string;
	status?: PaymentStatus;
	amount_collected: number | null;
	amount_to_be_paid: number; // it refers to payable amount + adjustment fees (which can only be calculated if payment succeeded)
	billing_address: BillingAddress | null;
	note: string | null;
	payment_failed_reason?: string;
	payment_options?: PaymentOptions;
	payment_method?: PaymentMethod;
	show_payment_summary?: boolean | null;
	show_payment_terms?: boolean | null;
	total?: QuoteTotal;
}

export interface DocumentPaymentSummaryAndTerm {
	show_payment_summary?: boolean;
	show_payment_terms?: boolean;
	custom_amount?: number | null;
	custom_percentage?: number | null;
}

export interface OrderFormQuoteClientContactSelectionDetail {
	readonly is_existing_client_found: boolean;
	readonly is_existing_contact_found: boolean;
}

export enum DocumentPaperSize {
	A4 = "a4",
	Letter = "letter"
}

export const defaultZomentumDocPaperSize = DocumentPaperSize.A4;

export class Document extends ArchievedWithSyncModel {
	active_editor_revision?: string | null = null;
	document_identifier = "";
	document_number?: any;
	primary_client_user_id = "";
	primary_client_user: ClientContact = new ClientContact();
	activities?: { data: Activity[] } = { data: new Array<Activity>() };
	can_approve = false;
	can_comment = false;
	can_edit = true;
	client_company_id = "";
	client_company_name = "";
	client_company_owner_id = "";
	comments?: { data: Comment[] } = { data: new Array<Comment>() };
	company_id = "";
	company?: UserCompany = new UserCompany();
	created: Date = new Date();
	custom_fields?: CustomField[];
	description?: string = "";
	document_email_reminder_details: DocumentAutoReminder | null =
		new DocumentAutoReminder();
	document_expiry_details: DocumentExpiry | null = new DocumentExpiry();
	document_type?: DocumentType = DocumentType.PROPOSAL;
	document_default_text_settings: DocumentDefaults =
		new DocumentDefaultDocument();
	id = "";
	is_link_enabled_for_all_recipients?: boolean = true;
	is_public_link_enabled?: boolean = false;
	is_quote_block_revamped?: boolean = false;
	is_quote_block_compact?: boolean = false;
	is_po_mandatory?: boolean = false;
	po_length?: number;
	link_copied = false;
	linked_entity_data: LinkedEntityData[] = new Array<LinkedEntityData>();
	merge_tag_custom_variables?: MergeTagVariable[];
	merge_tag_system_variables?: MergeTagVariable[];
	merge_tag_view_preference?: EmptyMergeTagViewPreference =
		EmptyMergeTagViewPreference.ReplaceWithEmptyString;
	name = "";
	needs_signature = false;
	opportunity_id: string | null = null;
	opportunity_name: string | null = null;
	opportunity_owner_id: string | null = null;
	paper_size?: DocumentPaperSize = defaultZomentumDocPaperSize;
	recipients: DocumentRecipient[] = new Array<DocumentRecipient>();
	revisions?: DocumentRevision[] = new Array<DocumentRevision>();
	secondary_client_user_ids: string[] = [];
	secondary_client_users: ClientContact[] = new Array<ClientContact>();
	signers_to_sign_before_current_user?: DocumentRecipient[];
	signers: DocumentRecipient[] = new Array<DocumentRecipient>();
	status: DocumentStatus = DocumentStatus.Draft;
	ship_to_address: Address | null = null;
	tax_categories: TaxCategory[] = new Array<TaxCategory>();
	tax_region_id: string | null = null;
	tax_regions: TaxRegion[] = new Array<TaxRegion>();
	total: QuoteTotal = new QuoteTotal();
	updated: Date = new Date();
	user_id = "";
	user: UserData = new UserData();
	payment?: DocumentPayment | null = null;
	commission?: number;
	allow_download = true;
	approval_required = false;
	approval_required_rules: DocumentApproval[] | null = null;
	comment_box_default_labels?: DocumentCommentBoxLabels =
		new DocumentCommentBoxLabels();
	is_pin_protected = false;
	pin_lock_status: DocumentPinStatus = DocumentPinStatus.Unlocked;
	is_pin_generation_blocked?: DocumentPinGenerationResponse;
	is_pin_approval_blocked?: VerifyDocumentPinResponse;
	is_onboarding_entity?: boolean;
	is_pdf_uploaded?: boolean;
	mandatory_custom_field_check_require = false;
	date_pattern?: string;
	po_number?: string | null;
	total_tax: number | null = null;
	total_discount: number | null = null;
	has_pdf_block?: boolean;
	quickbooks_estimate_doc_numbers?: string[];
	quickbooks_invoice_doc_numbers?: string[];
	quickbooks_sync_status?: string;
	is_discount_allowed?: boolean | null = null;
	quote_client_contact_selection_detail?: null | OrderFormQuoteClientContactSelectionDetail;
	is_deleted?: boolean = false;
	gof_submitted_client_name?: string | null = null;
	gof_submitted_client_user_name?: Name | null = null;
	payment_summary_and_term?: DocumentPaymentSummaryAndTerm | null = null;
	deprecated_url?: string;
	editor_details?: {
		document_id: string;
		cloned_document_id?: string | null;
		is_free_doc: boolean;
		parent_revision_id?: string | null;
	} | null = null;
	is_single_block?: boolean = false;
	signer_input_fields: { field_id: string; value: string }[] = [];

	static getLatestRevision(document: Document): DocumentRevision | undefined {
		return { ...(document.revisions || [])[0] };
	}

	static getAcknowledgeSign(document: Document): boolean | undefined {
		return document.revisions?.[0]?.signature_details?.signers?.every(
			signer => signer.signed
		);
	}

	static getLatestRevisionBlockList(document: Document): string[] {
		// TODO: add isEditable check here and then it will be possible for this to
		// return null
		const latestRevision = Document.getLatestRevision(document);
		return latestRevision?.block_list || [];
	}

	static getRecipientEmails = (document: Document): string[] =>
		document?.recipients?.length > 0
			? (document.recipients
					.filter(
						recipient =>
							recipient?.id?.length > 0 &&
							recipient.id !== document.user_id
					)
					.map(recipient => recipient?.email)
					.filter(email => !!email?.length) as string[])
			: new Array<string>();

	static getRecipientIds = (document: Document): string[] =>
		document?.recipients?.length > 0
			? document.recipients
					.filter(
						recipient =>
							!!recipient?.email?.length &&
							recipient.id !== document.user_id
					)
					.map(recipient => recipient.id)
			: new Array<string>();
}

export interface DocumentApprovalSendRequest {
	document_id: string;
}

export interface DocumentApprovalVerdictRequest {
	document_id: string;
	is_approved: boolean;
	comments: string;
}

export interface LinkedEntityData {
	entity_id: string;
	entity_type: ZomentumEntities;
	entity_processed: boolean;
}

export class DocumentsRequest {
	filters: TableQueryFilter[];
	keywords: string | null;
	includeEntities: ChildEntityHydrationParams[] | undefined;
	sortQuery?: string;
	page?: number;
	limit?: number;
	constructor(
		filters: TableQueryFilter[],
		keywords: string | null,
		includeEntities?: ChildEntityHydrationParams[],
		sortQuery?: string,
		page?: number,
		limit?: number
	) {
		this.filters = filters;
		this.keywords = keywords;
		this.includeEntities = includeEntities;
		this.sortQuery = sortQuery;
		this.page = page;
		this.limit = limit;
	}
}

export type EditDocumentDetailsRequest = Omit<Document, "recipients"> & {
	recipients: EditDocumentDetailsRecipient[];
};

export interface EditDocumentDetails {
	id: string;
	name: string;
	description?: string;
	opportunity_id?: string | null;
	client_company_id: string;
	document_identifier?: string | null;
	recipients: EditDocumentDetailsRecipient[];
	merge_tag_custom_variables?: MergeTagVariable[];
	user_id: string;
	document_expiry_details: DocumentExpiry | null;
	document_email_reminder_details: DocumentAutoReminder | null;
	merge_tag_view_preference?: EmptyMergeTagViewPreference;
	custom_fields?: CustomField[];
	tax_region_id: string | null;
	document_type?: DocumentType;
	link_copied?: boolean;
	is_pin_protected: boolean;
	allow_download: boolean;
	document_number?: string;
	po_number?: string | null;
	payment_summary_and_term?: DocumentPaymentSummaryAndTerm | null;
	ship_to_address: Address | null;
	is_quote_block_revamped?: boolean;
	is_quote_block_compact?: boolean;
	paper_size?: DocumentPaperSize;
}

export class EditDocumentRequest {
	document: EditDocumentDetails;
	included_child_entities: ChildEntityHydrationParams[] =
		new Array<ChildEntityHydrationParams>();

	constructor(
		currDocument: Document | EditDocumentDetailsRequest,
		included_child_entities: ChildEntityHydrationParams[]
	) {
		this.document = this.stripRedundantFields(currDocument);
		this.included_child_entities = included_child_entities;
	}

	// stripping out all the unwanted fields from document
	// any extra fields to be sent to Backend must be added here
	stripRedundantFields = (
		document: Document | EditDocumentDetailsRequest
	): EditDocumentDetails => {
		const cleanedRecipients: EditDocumentDetailsRecipient[] = [];

		// using forEach because Typescript won't allow map or reduce on different signatures
		document.recipients.forEach(recipient => {
			cleanedRecipients.push({
				id: recipient.id,
				user_type: recipient.user_type,
				blocks_to_sign: recipient.blocks_to_sign ?? []
			});
		});

		return {
			id: document.id,
			name: document.name,
			description: document.description,
			opportunity_id: document.opportunity_id,
			client_company_id: document.client_company_id,
			document_identifier: document.document_identifier,
			recipients: cleanedRecipients,
			merge_tag_custom_variables: document.merge_tag_custom_variables,
			user_id: document.user_id,
			document_expiry_details: document.document_expiry_details,
			document_email_reminder_details:
				document.document_email_reminder_details,
			merge_tag_view_preference: document.merge_tag_view_preference,
			custom_fields: document.custom_fields?.filter(
				customField =>
					customField.field_type !== CustomFieldType.FormulaField
			),
			tax_region_id: document.tax_region_id,
			document_type: document.document_type,
			link_copied: document.link_copied,
			is_pin_protected: document.is_pin_protected,
			allow_download: document.allow_download,
			document_number: document.document_number,
			po_number: document.po_number,
			payment_summary_and_term: document.payment_summary_and_term,
			ship_to_address: document.ship_to_address,
			is_quote_block_revamped: document.is_quote_block_revamped,
			is_quote_block_compact: document.is_quote_block_compact,
			paper_size: document.paper_size
		};
	};
}

export interface MissingMandatoryFieldsFormValues {
	name?: string;
	recipients?: string[];
	client_company_id?: string;
	tax_region_id?: string;
	user_id?: string;
	custom_field_form_values?: Record<string, string>;
	email_fields_form_values: {
		[id: string]: {
			email: string;
		};
	};
}
export class DocumentsResponse {
	base = "";
	filtered_count_map: DocumentCount = new DocumentCount();
	data: Document[] = new Array<Document>();
}

export class DocumentDetailsRequest {
	documentId = "";
	blockLimit: number | null = null;
	blockIdCursor: string | null = null;
	appendQueryParamAuth?: boolean;
	constructor(
		documentId = "",
		blockLimit: number | null,
		blockIdCursor: string | null = null,
		appendQueryParamAuth?: boolean
	) {
		this.documentId = documentId;
		this.blockLimit = blockLimit;
		this.blockIdCursor = blockIdCursor;
		this.appendQueryParamAuth = appendQueryParamAuth;
	}
}

export class MailRecipient {
	company_id = "";
	created: Date = new Date();
	department = "";
	email = "";
	entity_type: ZomentumEntities | null = null;
	id = "";
	name: Name = new Name();
	social: Social = new Social();
	unqualified_reason = "";
	updated: Date = new Date();
}

export class MailRecipientResponse {
	data: MailRecipient[] = new Array<MailRecipient>();
	links: Links = new Links();
}

export enum UserType {
	User = "user",
	ClientUser = "client_user"
}

export class DocumentRecipient {
	id: string;
	created: Date;
	updated: Date;
	name: Name;
	email: string | null;
	phone?: string;
	blocks_to_sign: string[];
	user_type: UserType;
	is_disabled?: boolean;

	constructor(
		id: string,
		created: Date,
		updated: Date,
		name: Name,
		email: string | null,
		userType: UserType,
		phone?: string,
		is_disabled?: boolean
	) {
		this.id = id;
		this.created = created;
		this.updated = updated;
		this.name = name;
		this.email = email;
		this.phone = phone;
		this.blocks_to_sign = new Array<string>();
		this.user_type = userType;
		this.is_disabled = is_disabled;
	}

	public static fromUser = (args: UserData) => {
		return new DocumentRecipient(
			args.id,
			args.created,
			args.updated,
			args.name,
			args.email,
			UserType.User,
			args.phone,
			args.is_disabled
		);
	};

	public static fromClientUser = (args: ClientContact) => {
		return new DocumentRecipient(
			args.id,
			args.created,
			args.updated,
			args.name,
			args.email,
			UserType.ClientUser,
			args.phone
		);
	};
}

export interface EmailRecipient {
	email: string | null;
	name: string;
	id: string;
}

export interface DocumentFormValues {
	name: string;
	template_id: string;
	opportunity_id: string;
	user_id: string;
	client_company_id: string;
	logs_url?: string;
	category: DocumentType;
	recipients: string[];
	tax_region_id: string | null;
	custom_field_form_values: Record<string, string>;
	document_type: DocumentType;
	po_number?: string | null;
	status?: string;
	notify_recipients: boolean;
	ship_to_address: string | null;
	editor_type?: string;
}
export interface AddMergeTagVariableFormValues {
	variable_name: string;
	value: string;
}

export class MailRecipientRequest {
	company_id = "";
	email = "";
	entity_id = "";
	entity_type: ZomentumEntities | null = null;
	name: Name = new Name();
}

export type EditDocumentDetailsRecipient = Omit<
	DocumentRecipient,
	"created" | "updated" | "name" | "email" | "phone"
> & {
	blocks_to_sign?: string[];
};
export type AddDocumentDetailsRecipient = Omit<
	EditDocumentDetailsRecipient,
	"created" | "updated" | "name" | "email" | "phone" | "blocks_to_sign"
>;

export interface AddDocumentRequest {
	name: string;
	template_id?: string | null;
	document_id?: string | null;
	opportunity_id?: string | null;
	user_id: string;
	document_type?: DocumentType;
	client_company_id?: string;
	recipients?: AddDocumentDetailsRecipient[];
	tax_region_id: string | null;
	custom_fields?: CustomField[];
	opportunity_pipeline_id?: string | null;
	qbr_representation_style?: QbrRepresentationStyle | null;
	assessment_representation_style?: AssessmentRepresentationStyle | null;
	mandatory_custom_field_check_require: boolean;
	po_number?: string | null;
	pdf_url?: string;
	block_ids_to_add?: string;
	ship_to_address: Address | null;
	cell_data?: RiskMatrixCellData[];
	likelihood_header_label?: string;
	severity_header_label?: string;
	severity_labels?: SeverityLabel[];
	likelihood_labels?: LikelihoodLabel[];
	legend_labels?: LegendLabel[];
	is_onboarding_entity?: boolean;
	is_pdf_uploaded?: boolean;
	paper_size?: DocumentPaperSize;
	is_single_block?: boolean;
}

export interface DynamicFormFieldRequestView {
	key: string;
	children?: DynamicFormFieldRequestView[];
	text_value?: string;
	numeric_value?: number;
	checkbox_value?: boolean;
	date_value?: string;
	drop_down_value?: string;
	multi_select_drop_down_value?: string[];
}

export interface DynamicFormRequestSectionView {
	label: string;
	key: string;
	fields: DynamicFormFieldRequestView[];
}
export interface DynamicFormCreateRequest {
	banner_text?: string;
	identifier: FormIdentifier;
	sections: DynamicFormRequestSectionView[];
}

export interface CloneDocumentRequest
	extends Omit<AddDocumentRequest, "template_id"> {
	name: string;
	document_id: string | null;
	opportunity_id?: string | null;
	client_company_id: string;
	document_type: DocumentType;
	recipients: AddDocumentDetailsRecipient[];
	mandatory_custom_field_check_require: boolean;
	po_number?: string | null;
}

export class UpdateDocumentCountRequest {
	documentAdded = false;
	documentEdited = false;
	documentDeleted = false;
	oldDocumentStatus: DocumentStatus | null = null;
	newDocumentStatus: DocumentStatus | null = null;
	constructor(
		documentAdded: boolean,
		documentEdited: boolean,
		documentDeleted: boolean,
		oldDocumentStatus: DocumentStatus | null,
		newDocumentStatus: DocumentStatus | null = null
	) {
		this.documentAdded = documentAdded;
		this.documentEdited = documentEdited;
		this.documentDeleted = documentDeleted;
		this.oldDocumentStatus = oldDocumentStatus;
		this.newDocumentStatus = newDocumentStatus;
	}
}

export interface UpdateBlockListSequenceRequest {
	draggableId: string;
	index: number;
	isNewQuoteBlockDesign: boolean;
	isQuoteBlockCompact?: boolean;
}

export interface UpdateMultipleBlockListSequenceRequest {
	blockList: BlockResponse[];
	index: number;
}

export interface BlockRepositionRequest {
	sourceIndex: number;
	destinationIndex: number;
}

export enum DocumentSidePanelEnum {
	EDIT = "EDIT",
	ISSUES = "ISSUES",
	COMMENTS = "COMMENTS",
	PEOPLE = "PEOPLE",
	ACTIVITY = "ACTIVITY",
	SETTINGS = "SETTINGS",
	PAYMENT = "PAYMENT",
	DOWNLOAD = "DOWNLOAD",
	QUOTE_SUMMARY = "QUOTE_SUMMARY",
	SAVED_BLOCKS = "SAVED_BLOCKS"
}

export interface DocumentPerson {
	name: Name;
	email: string;
	isPrimary: boolean;
}

export interface TinyState {
	Bold?: boolean;
	Italic?: boolean;
	Underline?: boolean;
	JustifyLeft?: boolean;
	JustifyCenter?: boolean;
	JustifyRight?: boolean;
	JustifyFull?: boolean;
	Indent?: boolean;
	Outdent?: boolean;
	ForeColor?: string;
	HiliteColor?: string;
	FontName?: string;
	FontSize?: string;
	FormatBlock?: string;
	InsertOrderedList?: string;
	InsertUnorderedList?: string;
	LineHeight?: string;
}

export const booleanTinyKeys: Partial<keyof TinyState>[] = [
	"Bold",
	"Italic",
	"Underline",
	"JustifyLeft",
	"JustifyCenter",
	"JustifyRight",
	"JustifyFull",
	"Indent",
	"Outdent",
	"InsertOrderedList",
	"InsertUnorderedList"
];
export const stringTinyKeys: Partial<keyof TinyState>[] = [
	"FontName",
	"FontSize",
	"LineHeight"
];

export interface MergeTagVariable {
	variable_type: MergeTagVariableType;
	base_variable_name: string;
	variable_name: string;
	value?: string;
	display_name: string;
	allowed_values?: string[];
	data_type?: MergeTagVariableDataType;
	is_editable?: boolean;
	is_input_variable?: boolean;
}

export enum MergeTagVariableDataType {
	DOUBLE = "double",
	DATE = "date",
	TEXT = "text",
	DROPDOWN = "dropdown",
	EMAIL = "email",
	NUMBER = "number",
	URL = "url"
}

export enum MergeTagVariableType {
	SIMPLE = "simple",
	MULTIPLE_NUMBERS = "multiple_numbers"
}

export enum EditOrCloneDocumentDrawerMode {
	EDIT = "edit",
	CLONE = "clone"
}

export interface AddInputFieldFormValues {
	assignee?: string;
	type: InputFieldType;
	label?: string;
	placeholder?: string;
	is_required: boolean;
	length: number;
	external_id: string | null;
}

export enum InputFieldType {
	Text = "Text",
	Date = "Date",
	CheckBox = "CheckBox"
}
export class InputField {
	type: InputFieldType = InputFieldType.Text;
	assignee?: string;
	label?: string;
	placeholder?: string;
	isRequired = true;
	length = 0;
	dataRole?: string;
	external_id: string | null = null;
}

export interface EmptyMergeTagResponse {
	present_and_empty_tags: string[];
}

export interface DownloadDocumentProductListRequest {
	downloadedFileName: string;
	documentId: string;
	revisionId: string;
}

export interface DownloadDocumentLogsRequest {
	downloadedFileName: string;
	documentId: string;
	logs: string[];
}

export enum PaymentStatus {
	Not_Inititated = "Not initiated",
	Paid = "Payment completed",
	Opt_Out = "Opt out",
	Failed = "Payment failed",
	Initiated = "Initiated"
}

export interface ClientPaymentData {
	billing_address: BillingAddress;
	note: string;
}
export interface MergeTagsInputScheme {
	key: string;
	value?: string | unknown;
}

export interface MergeTagInput {
	entity_id: string;
	entity_type: string;
	merge_tags: MergeTagsInputScheme[];
}

export enum MergeTagPreference {
	ENTRY = "entry",
	BLANK = "blank",
	REPLACE = "replace"
}

export enum MergeTagInputDataType {
	DATE = "date",
	DOUBLE = "double",
	DROPDOWN = "dropdown",
	TEXT = "text"
}

export interface MergeTagSubRequest {
	key: string;
	value: string;
}

export interface MergeTagUpdateValueErrors {
	merge_tag_sub_requests: MergeTagSubRequest[];
	error: string;
}

export interface MergeTagValueUpdateResponse {
	merge_tag_errors: MergeTagUpdateValueErrors[];
	grouped_merge_tag_errors: MergeTagUpdateValueErrors[];
}

export const getSelectableDocumentBulkUpdateFields = (
	customFields: CustomField[]
) => {
	return [
		{ display_value: "Title", id_value: "name" },
		{
			display_value: "Document Type",
			id_value: "document_type"
		},
		{ display_value: "Owner", id_value: "user_id" }
	].concat([
		...customFields
			?.filter(
				customField =>
					customField.is_value_editable_from_ui !== false &&
					!customField.system_field_id
			)
			.map(customField => {
				return {
					display_value: customField.display_name,
					id_value: `custom_fields.${customField.id}`
				};
			})
	]);
};

export interface TemplateRecipient {
	user_type: string;
	index: number;
	blocks_to_sign: string[];
}

export enum ConnectBoosterPaymentVersion {
	THREE_STEP = "three_step",
	DIRECT_POST = "direct_post"
}

export enum AchSecCode {
	PPD = "PPD",
	WEB = "WEB",
	TEL = "TEL",
	CCD = "CCD",
	POP = "POP",
	ARC = "ARC"
}

export enum AchAccountType {
	CHECKING = "checking",
	SAVINGS = "savings"
}

export enum AchEntityType {
	PERSONAL = "personal",
	BUSINESS = "business"
}

export enum PaymentMethod {
	CREDIT_CARD = "CREDIT_CARD",
	ACH = "ACH"
}

export interface ConnectBoosterPaymentRequest {
	ach_account_type?: AchAccountType;
	ach_entity_type?: AchEntityType;
	ach_sec_code?: AchSecCode;
	payment_method: PaymentMethod;
	transaction_token?: string;
	payment_version: ConnectBoosterPaymentVersion;
}

export interface MakePaymentPayload {
	documentId: string;
	paymentMethod: PaymentMethod;
	optOut: boolean;
	paymentVersion: ConnectBoosterPaymentVersion;
	achSecCode?: AchSecCode;
	achAccountType?: AchAccountType;
	achEntityType?: AchEntityType;
	transactionToken?: string;
}

export interface CollectJSResponse {
	token: string;
	merchantid: string;
	merchantName: string;
	currencies: string[];
	cardBrands: string[];
	applePay: {
		currencies: string[];
		cardBrands: string[];
	};
	testMode: boolean;
	allowedApplePayDomains: string[];
}

export enum Gateway {
	ConnectBooster = "ConnectBooster",
	Stripe = "stripe",
	Payments = "Payments"
}

export class PaymentOption {
	constructor(
		public gateway: Gateway,
		public fixed_amount: number | null,
		public percentage: number | null,
		public total_adjustment_fees?: number
	) {}

	static getDefaultPaymentOption = (availableGateway?: Gateway) => {
		return !!availableGateway
			? new PaymentOption(availableGateway, null, null, 0)
			: undefined;
	};

	static getPaymentOptionType = (paymentOption: PaymentOption) => {
		if (paymentOption?.percentage) return PaymentOptionType.Percentage;
		return PaymentOptionType.FixedAmount;
	};
}

export enum PaymentOptionType {
	FixedAmount = "fixed_amount",
	Percentage = "percentage"
}

export interface PaymentOptions {
	credit_card: PaymentOption | null;
	ach: PaymentOption | null;
}

export interface DocumentPaymentRequest {
	custom_amount?: number | null;
	custom_percentage?: number | null;
	gateway?: string;
	is_optional?: boolean;
	payer?: string;
	payment_options?: PaymentOptions;
	show_payment_summary?: boolean;
	show_payment_terms?: boolean;
}
export interface UpdatePoDetailsPayload {
	document_id: string;
	po_number: string | null;
}

export interface UpdatePoDetailsResponse {
	status: boolean;
	force_update_external_editor: boolean;
}

export interface SignatureBlocksAssignProperty {
	signer_id: string;
	block_id: string;
}

export class BulkAssignSignatureBlocksRequest {
	signees: SignatureBlocksAssignProperty[] = [];

	constructor({
		signer_id,
		block_ids
	}: {
		signer_id: string;
		block_ids: string[];
	}) {
		const assignRequest: SignatureBlocksAssignProperty[] = block_ids.map(
			id => ({
				signer_id: signer_id,
				block_id: id
			})
		);

		return {
			signees: assignRequest
		};
	}
}

export enum EDocumentMenuItems {
	PUBLISHANDSEND = "Publish & Send",
	UNPUBLISH = "Unpublish",
	SEND = "Send document",
	SHAREVIALINK = "Share via link",
	EDITDETAILS = "Edit document details",
	APPROVE = "Approve document",
	PREVIEW = "Preview",
	PREVIEWASPDF = "Preview as PDF",
	NEWTEMPLATE = "Save as new template",
	CLONE = "Clone document",
	DOWNLOAD = "Download document (.pdf)",
	DOWNLOADPRODUCTLIST = "Download product list (.csv)",
	UPDATESTATUS = "Update status manually",
	PUSHCHANGESTOPSA = "Push changes to PSA",
	QUICKBOOKSYNC = "Sync with Quickbooks Online",
	FORCECANCELSIGNATURE = "Force Cancel Signature",
	DELETE = "Delete document"
}

export enum DocumentRecipientSelectActions {
	SELECTED = "selected",
	DESELECTED = "deselected"
}
export interface PaymentSummary {
	commission: number | null;
	total: QuoteTotal | null;
	total_tax: number | null;
	total_discount: number | null;
	total_adjustment_fees: PaymentOptions | null;
	margin_percentage?: number | null;
	margin?: number | null;
}

export enum PaymentAmountType {
	CUSTOM_AMOUNT = "custom_amount",
	CUSTOM_PERCENTAGE = "custom_percentage",
	GRAND = "grand"
}

export interface QuoteBlockProductDiscountRequest {
	totalSellPrice: number;
	discount?: number;
	discountType: DiscountType;
	discountPercentage?: number;
	sell_price: number;
	quantity: number;
}
export interface QuoteBlockProductSetupPriceDiscountRequest {
	totalSellPrice: number;
	discount?: number;
	discountType: DiscountType;
	discountPercentage?: number;
}
export interface QuickbooksLineItems {
	readonly line_item_id: string;
	readonly line_item_name: string;
	readonly tax_code_id: string;
	readonly percent?: number;
	readonly tax_category?: number;
}
export interface QuickbooksEstimatesInfo {
	readonly estimate_id: string;
	readonly estimate_name: string;
	readonly estimate_tax_info: QuickbooksTaxCode;
	readonly line_items_tax_info: QuickbooksTaxCode;
	readonly order: number;
}
export interface QuickbooksExistingInfo {
	readonly customer_ref: CommonNameValuePair;
	readonly push_mode: string;
	readonly estimates: QuickbooksEstimatesInfo[];
}
export interface QuickbooksDocumentSyncFormData {
	readonly customers: CommonNameValuePair[];
	readonly is_sales_tax_enabled: boolean;
	readonly is_automatic_sales_tax_enabled: boolean;
	readonly default_push_mode: string;
	readonly tax_info: QuickbooksTaxInfoList[];
	readonly line_items_info: QuickbooksLineItems[];
	readonly existing_info: QuickbooksExistingInfo | null;
	readonly country?: string;
}
export interface QuickbooksCreateEstimates {
	readonly estimate_tax_info: QuickbooksTaxCode | null;
	readonly line_items: string[] | null;
	readonly line_items_tax_info: QuickbooksTaxCode | null;
	readonly order: number;
}

export interface QuickbooksUpdateEstimates {
	readonly estimate_id: string;
	readonly estimate_name: string;
	readonly estimate_tax_info: QuickbooksTaxCode | null;
	readonly line_items: string[] | null;
	readonly line_items_tax_info: QuickbooksTaxCode | null;
	readonly order: number;
}

export class QuickbooksFormSyncRequest {
	push_mode = "";
	customer_ref: CommonNameValuePair | null = null;
	create_estimates: QuickbooksCreateEstimates[] =
		new Array<QuickbooksCreateEstimates>();
	update_estimates: QuickbooksUpdateEstimates[] =
		new Array<QuickbooksUpdateEstimates>();
	delete_estimates: string[] = new Array<string>();
}

export class UpdateOrderFormPublishStatusRequest {
	document_status: DocumentStatus.Published | DocumentStatus.Unpublished =
		DocumentStatus.Unpublished;
}

export class UpdateDocumentLinkStatusRequest {
	link_enabled_for_all_recipients: boolean;
	public_link_enabled: boolean;

	constructor(
		link_enabled_for_all_recipients: boolean,
		public_link_enabled: boolean
	) {
		this.link_enabled_for_all_recipients = link_enabled_for_all_recipients;
		this.public_link_enabled = public_link_enabled;
	}
}

export class OrderFormRequestorDetailsToClientDetailsRequest {
	client_id_to_shift: string | null = null;
	delete_new_client = false;
	keep_matched_client?: boolean = false;

	constructor(
		client_id_to_shift: string | null,
		delete_new_client: boolean,
		keep_matched_client?: boolean
	) {
		this.client_id_to_shift = client_id_to_shift;
		this.delete_new_client = delete_new_client;
		if (keep_matched_client !== undefined) {
			this.keep_matched_client = keep_matched_client;
		}
	}
}

export class SubmitOrderFormRequest {
	name = "";
	last_name = "";
	client_company_name?: string = "";
	phone: string | null = null;
	email?: string | null = null;
	billing_address: Address = new Address();

	shipping_addresses: Address[] = new Array<Address>();
	owner_id: string | null = null;
	client_company_number?: string | null = null;
	fax: string | null = null;
	website: string | null = null;

	linked_in_url: string | null = null;
	facebook_url: string | null = null;
	twitter_url: string | null = null;
	market?: string | null = null;
	company_type: string = ClientType.Prospect;

	source: string | null = null;
	campaign: string | null = null;
	date_acquired: moment.Moment = moment();
	employee_size?: number | null = null;
	tax_region_id: string | null = null;

	mobile?: string | null = null;

	custom_fields?: CustomField[] = new Array<CustomField>();

	po_number?: string = "";
	blocks_metadata_gof?: GOFBlockChanges[];
	total?: QuoteTotal;
}

export interface QuickbooksSyncFormDataParams {
	formValues: QuickbooksFormSyncRequest;
	documentId: string;
}

export interface QuickbooksCreateCustomerRequest {
	client_company_id: string;
	client_user_id: string;
}

export interface QuickbooksSyncFormDataValues {
	customer_ref: string;
	contact_ref: string;
	push_mode: string;
	create_estimates: Record<string, string>;
	single_estimate?: string;
	tax_info: Record<string, Record<string, string>>;
}

export const ConnectBoosterPaymentOption = {
	name: "ConnectBooster",
	value: Gateway.ConnectBooster
};

export const StripePaymentOption = {
	name: "Stripe",
	value: Gateway.Stripe
};

export const ZMPaymentOption = {
	name: "Zomentum Payments",
	value: Gateway.Payments
};

export interface DocumentFrameProps {
	readonly refreshData?: () => void;
	readonly hasEditPermission?: boolean;
	readonly revisionHistoryContainerRef: React.RefObject<HTMLDivElement>;
	readonly presenceContainerRef: React.RefObject<HTMLDivElement>;
	readonly commentsArchiveContainerRef: React.RefObject<HTMLDivElement>;
}

export interface UsePublishOrderFormResponse {
	publishOrderForm: (
		mergeTagPreference?: MergeTagPreference
	) => Promise<void>;
	unPublishOrderForm: () => Promise<void>;
	PublishOrderFormModal: ReactElement;
}

export enum OrderFormButtonInnerText {
	SUBMIT = "Submit Order",
	PAY_AND_SUBMIT = "Pay & Submit Order"
}

export enum RegularDocumentButtonInnerText {
	APPROVE = "Approve",
	PAY_AND_APPROVE = "Pay & Approve"
}

export enum SignDocumentButtonInnerText {
	SIGN_AND_APPROVE = "Sign and Approve",
	PAY_AND_SIGN = "Pay and Sign Document",
	SIGN = "Sign Document",
	CANCEL_SIGN = "Cancel Signature"
}

export enum ClientMatchOptions {
	KEEP_MATCHED = "keep_matched",
	KEEP_NEW_CLIENT = "keep_new_lient",
	CHOOSE_ANOTHER_CLIENT = "choose_another",
	CREATE_NEW_AND_SHIFT = "create_new_and_shift"
}

export enum PaymentConfigurationPanels {
	ONLINE_PAYMENT = "online_payment",
	PAYMENT_TERMS = "payment_terms",
	DISPLAY_ON_DOCUMENT = "display_on_document"
}

export interface DeleteDocumentProps {
	readonly deleteDocument?: (document: Document) => Promise<void>;
	readonly refreshData?: () => void;
	readonly isDocumentRefreshRequired?: boolean;
}

export interface OpenAiResponse {
	readonly data: string[];
}

export interface OpenAIQueryHistory {
	query: string;
	created: string;
}
export interface OpenAIQueryHistoryResponse {
	data: OpenAIQueryHistory[];
}
export interface SendDocumentForSignatureRequest {
	documentId: string;
	userId: string | null;
	clientUserId: string | null;
	pdfBlob: Blob;
}

export interface AIWriterTrailResponse {
	is_trial_for_ai_writer_enabled: boolean;
	trial_expire_at: Date;
}

export interface AutotaskProductValidationView {
	product_id: string;
	name: string;
	item_id: string;
	issue: string;
	/** optional sync id from PSA */
	sync_id?: string;
	/** optional link to item in PSA */
	sync_source_url?: string;
	suggestion: string;
}

export interface RecentDocumentRecipientView {
	readonly recipient_id: string;
	readonly user_type: string;
	readonly blocks_to_sign: string[];
}

export interface RecentDocumentItemView {
	readonly id: string;
	readonly name: string;
	readonly client_company_id?: string | null;
	readonly opportunity_id?: string | null;
	readonly recipients: RecentDocumentRecipientView[];
}

export interface RecentDocumentRequest {
	readonly opportunity_ids?: string[];
	readonly client_company_ids?: string[];
	readonly client_user_ids?: string[];
}

export interface RecentDocumentResponse {
	readonly data: RecentDocumentItemView[];
	readonly filtered_count_map?: Record<string, number> | null;
	readonly errors?: string[] | null;
}
